import * as React from 'react';
import * as _ from 'lodash';
import { CorpuseDocumentItem } from '../CorpuseDocumentItem/CorpuseDocumentItem';
import ProjectLeftPanel from '../../../components/ProjectLeftPanel/ProjectLeftPanel';
import ProjectLeftPanelTitle from '../../../components/ProjectLeftPanel/ProjectLeftPanelTitle';
import ProjectLeftPanelList from '../../../components/ProjectLeftPanel/ProjectLeftPanelList';
import ProjectLeftPanelListItem from '../../../components/ProjectLeftPanel/ProjectLeftPanelListItem';
import { ICorpusDocument } from '../../../../models/ICorpusDocument';
import CorpusDocumentsTree from '../CorpusDocumentsTree/CorpusDocumentsTree';
import { Resizer } from '../../../components/Resizer/Resizer';
import CollapsiblePanel from '../../../components/CollapsiblePanel/CollapsiblePanel';
import isLocalStorageAvailable from '../../../../services/mini/isLocalStorageAvailable';
import TextFieldWrapper from '../../../components/TextFieldWrapper/TextFieldWrapper';
import { getLastPart } from '../corpusTreeHelpers';

type CorpusDocumentsPanelProps = {
    documents: ICorpusDocument[],
    isLoading: boolean,
    searchInProcess: boolean,
    isSearchActive: boolean,
    selectedDocument: ICorpusDocument
    changeSelectedDocument: (document: ICorpusDocument) => void
    changeSelectedDocumentInSearch: (document: ICorpusDocument) => void
}

type CorpusDocumentsPanelState = {
    panelMinimized: boolean,
    query: string
    documents: ICorpusDocument[],
    documentUpdateTs: number
}

const CORPUS_DOCS_PANEL_COLLAPSED_LS_KEY = 'corpus-docs-panel__collapsed';

function normalizeString(str) {
    return str.toLowerCase()
        .replace(/[-&=?#/.]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}

function searchWithPartialMatching(url, query) {
    const normalizedUrl = normalizeString(url);
    const normalizedQueryWords = normalizeString(query).split(' ');

    return normalizedQueryWords.every(word => normalizedUrl.includes(word));
}

class CorpusDocumentsPanel extends React.Component<CorpusDocumentsPanelProps, CorpusDocumentsPanelState> {

    constructor(props) {
        super(props);

        let panelMinimized = false;

        if (isLocalStorageAvailable()) {
            panelMinimized = localStorage.getItem(CORPUS_DOCS_PANEL_COLLAPSED_LS_KEY) === 'true';
        }

        this.state = {
            panelMinimized,
            query: '',
            documents: [],
            documentUpdateTs: null
        };
    }

    componentDidMount(): void {
        this.setState({ documents: this.props.documents, documentUpdateTs: Date.now() });
    }

    resizer = React.createRef<Resizer>();
    collapsiblePanelRef = React.createRef<HTMLDivElement>();

    goToDocCb = (document: ICorpusDocument) => {
        const { changeSelectedDocument, changeSelectedDocumentInSearch, isSearchActive } = this.props;
        if (isSearchActive) {
            const elAnchor = window.document.getElementById(`line-${document?.lineIdx || 0}`);
            if (elAnchor) {
                elAnchor.scrollIntoView({ behavior: 'smooth' });
                changeSelectedDocumentInSearch(document);
            }
        } else {
            changeSelectedDocument(document);
        }
    }

    onResizerStateChange = (state) => {
        if (state.isOpened !== this.state.panelMinimized) {
            this.setState({
                panelMinimized: !state.isOpened,
            });
        }
    };

    collapsePanel = () => {
        this.collapsiblePanelRef.current?.setAttribute('style', '');
        this.resizer.current?.collapse(null);
    }
    expandPanel = () => {
        this.resizer.current?.toggle(null);
    }


    onResizeStart = () => {
    }

    onResizeEnd = ({ width }) => {
        if (width < 60) {
            this.collapsePanel();
            this.setState({ panelMinimized: true });
            if (isLocalStorageAvailable()) {
                localStorage.setItem(CORPUS_DOCS_PANEL_COLLAPSED_LS_KEY, 'true');
            }
        } else {
            this.setState({ panelMinimized: false });
            this.resizer.current?.setOpenedState(true);
            if (isLocalStorageAvailable()) {
                localStorage.setItem(CORPUS_DOCS_PANEL_COLLAPSED_LS_KEY, 'false');
            }
        }
        return true;
    }

    handleQueryChange = (e) => {
        const val = e.target.value;
        this.setState({ query: val });
        this.performSearch(val);
    }
    handleSearchClose = (e) => {
        this.setState({ query: '' });
        this.performSearch('');
    }

    performSearch = _.debounce((query) => {
        const { documents } = this.props;
        if (query) {
            this.setState({ documents: documents?.filter(doc => searchWithPartialMatching(getLastPart(doc.url.toLowerCase()), query)), documentUpdateTs: Date.now() });
        } else {
            this.setState({ documents, documentUpdateTs: Date.now() });
        }
    }, 1000);

    render() {
        const { isLoading, selectedDocument, searchInProcess, isSearchActive } = this.props;
        const { documents, documentUpdateTs } = this.state;
        const { panelMinimized, query } = this.state;
        const showTree = documents.every(doc => !!doc.url);

        const documentsTitle = `Documents${documents?.length > 0 ? `: ${documents?.length}` : ''}`;

        return (
            <Resizer
                lsKey={'CorpusDocumentResizerRoot'}
                ref={this.resizer}
                y={100}
                x={25}
                min={0}
                max={50}
                isOpened={!panelMinimized}
                onResizeEnd={this.onResizeEnd}
                onStateChange={this.onResizerStateChange}
                onResizeStart={this.onResizeStart}
                styleName={'corpus-document-list__resizer'}
                divider="right">
                <div ref={this.collapsiblePanelRef} className="corpus-document-list__collapsible-wrapper">
                    <CollapsiblePanel
                        title={documentsTitle}
                        direction="left"
                        id={CORPUS_DOCS_PANEL_COLLAPSED_LS_KEY}
                        panelClassName="corpus-document-list-panel"
                        collapsed={panelMinimized}
                        onPanelCollapsed={() => {
                            this.setState({ panelMinimized: true });
                            this.collapsePanel();
                        }}
                        onPanelExpanded={() => {
                            this.setState({ panelMinimized: false });
                            this.expandPanel();
                        }}>
                        <>
                            <ProjectLeftPanel hasData={!isLoading} noMinWidth>
                                <ProjectLeftPanelTitle title={documentsTitle} />
                                <div className={`corpus-document-list-panel__search ${query.length > 0 ? 'active' : ''}`}>
                                    <i className="icon icon-alan-search" />
                                    <TextFieldWrapper
                                        name="query"
                                        id="query"
                                        placeholder="Search"
                                        value={query}
                                        autoComplete="off"
                                        onChange={this.handleQueryChange}
                                        onBlur={() => { }}
                                        onEsc={this.handleSearchClose}
                                        skipOnChangeAfterOnBlur={true}
                                        height={20}
                                    />
                                    {query.length > 0 && <i className="icon icon-alan-close-rounded" onClick={this.handleSearchClose} />}
                                </div>
                                <ProjectLeftPanelList>
                                    {!showTree ?
                                        documents?.map(doc => {
                                            return <ProjectLeftPanelListItem
                                                key={doc.id}
                                                selected={selectedDocument?.name === doc.name}>
                                                <CorpuseDocumentItem
                                                    {...doc}
                                                    onClick={this.goToDocCb}
                                                />
                                            </ProjectLeftPanelListItem>;
                                        }) :
                                        <>
                                            {selectedDocument ?
                                                <>
                                                    {documents?.length > 0 ?
                                                        <CorpusDocumentsTree
                                                            documents={documents}
                                                            key={documentUpdateTs}
                                                            documentUpdateTs={documentUpdateTs}
                                                            onClick={this.goToDocCb}
                                                            selectedDocument={selectedDocument}
                                                            autoExpandDepth={(query?.length > 0 || isSearchActive) ? 100 : 8} /> :
                                                        <div className="corpus-document-list-panel__no-docs">No documents</div>
                                                    }
                                                </>
                                                :
                                                <></>
                                            }
                                        </>}
                                </ProjectLeftPanelList>
                            </ProjectLeftPanel>
                            {searchInProcess && <div className={`corpus-document-list-panel__disbaled`} />}
                        </>
                    </CollapsiblePanel>
                </div>
            </Resizer>
        );
    }
}

export default CorpusDocumentsPanel;
